import { useState, useEffect, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { 
  Search, Info, X, ChevronDown, Activity, ClipboardCheck, 
  ClipboardList, Beaker, Target, Eye, Users, 
  ShieldCheck, Scale, Award, Briefcase, Heart, Sparkles 
} from "lucide-react";
import labData from "../data/labsysoContent.json";
import LabsysoFeedbackWidget from "../components/labsyso/LabsysoFeedbackWidget";
import { LABSYSO_FEEDBACK } from "../config/labsysoFeedback";
import { useCmsDoc } from "../hooks/useCmsDoc";
import ConsentAwareEmbed from "../components/ConsentAwareEmbed";

const LabsysoPage = () => {
  const labFallback = useMemo(
    () => ({
      hero: labData.labsyso.hero,
      socialMedia: labData.labsyso.socialMedia,
      about: labData.labsyso.about,
      accreditations: labData.labsyso.accreditations,
      preparations: labData.labsyso.preparations || [],
    }),
    []
  );
  const examsFallback = useMemo(
    () => ({ categories: labData.labsyso.categories || [] }),
    []
  );
  const { data: labCms } = useCmsDoc("labsyso", "lab", labFallback);
  const { data: examsCms } = useCmsDoc("labsyso", "exams", examsFallback);

  const labsyso = {
    ...labData.labsyso,
    ...(labCms || {}),
    categories: examsCms?.categories || labData.labsyso.categories,
  };

  const labsysoCatalogUrl = labsyso?.accreditations?.catalog?.embedUrl || "";
  const [activeMainTab, setActiveMainTab] = useState("examenes");
  const [searchTerm, setSearchBar] = useState("");
  const [activeCatId, setActiveCatId] = useState(labsyso.categories[0]?.id);
  const [showPrep, setShowPrep] = useState(null);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [isDesktop, setIsDesktop] = useState(() => typeof window !== "undefined" ? window.innerWidth >= 1024 : true);

  const activeCategoryId = activeCatId ?? (isDesktop ? labsyso.categories[0]?.id : null);

  useEffect(() => {
    document.body.setAttribute("data-page-theme", "labsyso");
    return () => document.body.removeAttribute("data-page-theme");
  }, []);

  useEffect(() => {
    const onResize = () => setIsDesktop(window.innerWidth >= 1024);
    window.addEventListener("resize", onResize, { passive: true });
    onResize();
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const handleCategoryToggle = (id) => {
    if (isDesktop) {
      setActiveCatId(id);
      return;
    }
    setActiveCatId((prevId) => (prevId === id ? null : id));
  };

  // Mapeo de iconos para valores
  const getValueIcon = (titulo) => {
    const icons = {
      "Ética": <Scale size={28} />,
      "Calidad": <Award size={28} />,
      "Responsabilidad": <Briefcase size={28} />,
      "Compromiso social": <Heart size={28} />,
      "Excelencia": <Sparkles size={28} />,
      "Trabajo en equipo": <Users size={28} />,
    };
    return icons[titulo] || <ShieldCheck size={28} />;
  };

  const filteredResults = useMemo(() => {
    if (!searchTerm) return [];
    return labsyso.categories.flatMap(cat => 
      cat.exams
        .filter(exam => exam.toLowerCase().includes(searchTerm.toLowerCase()))
        .map(exam => ({ name: exam, categoryTitle: cat.title }))
    );
  }, [searchTerm, labsyso.categories]);

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="bg-[#fafcfb] min-h-screen font-body pb-20">
      
      {/* 1. HERO SECTION */}
      <header className="relative pt-24 pb-12 bg-white overflow-hidden border-b border-[#211c52]/5">
        <div className="absolute inset-0 z-0 opacity-10">
          <img src={labsyso.hero.labsyso_logo_multi} className="w-full h-full object-cover" alt="Fondo" />
        </div>
        <div className="container mx-auto px-10 relative z-10 text-center">
            <motion.div initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} className="mb-6 flex justify-center">
              <img src={labsyso.hero.labsyso_logo} className="h-24 md:h-32 w-auto object-contain drop-shadow-xl" alt="LABSYSO Logo" />
            </motion.div>
            <motion.p className="text-[#211c52] text-lg font-medium max-w-2xl mx-auto leading-relaxed">
                {labsyso?.hero?.subtitle}
            </motion.p>
        </div>
      </header>

      {/* 2. SELECTOR DE TABS */}
      <div className="container mx-auto px-6 mt-10 mb-10">
        <div className="flex flex-wrap justify-center gap-2 md:gap-4 p-1.5 bg-gray-100 rounded-[24px] w-fit max-w-full mx-auto shadow-inner">
            <button
              onClick={() => setActiveMainTab("examenes")}
              className={`flex items-center gap-2 md:gap-3 px-5 md:px-8 py-3 md:py-4 rounded-[20px] font-bold text-xs md:text-sm transition-all cursor-pointer ${
                activeMainTab === "examenes"
                ? "bg-[#211c52] text-white shadow-lg scale-105"
                : "text-gray-500 hover:text-[#211c52]"
              }`}
            >
              <ClipboardList size={16} /> Exámenes
            </button>
            <button
              onClick={() => setActiveMainTab("laboratorio")}
              className={`flex items-center gap-2 md:gap-3 px-5 md:px-8 py-3 md:py-4 rounded-[20px] font-bold text-xs md:text-sm transition-all cursor-pointer ${
                activeMainTab === "laboratorio"
                ? "bg-[#211c52] text-white shadow-lg scale-105"
                : "text-gray-500 hover:text-[#211c52]"
              }`}
            >
              <Beaker size={16} /> Laboratorio
            </button>
            <button
              onClick={() => setActiveMainTab("credenciales")}
              className={`flex items-center gap-2 md:gap-3 px-5 md:px-8 py-3 md:py-4 rounded-[20px] font-bold text-xs md:text-sm transition-all cursor-pointer ${
                activeMainTab === "credenciales"
                ? "bg-[#211c52] text-white shadow-lg scale-105"
                : "text-gray-500 hover:text-[#211c52]"
              }`}
            >
              <Award size={16} /> Credenciales
            </button>
        </div>
      </div>

      <main className="container mx-auto px-6">
        <AnimatePresence mode="wait">
          {activeMainTab === "examenes" ? (
            /* --- VISTA EXÁMENES (Buscador y Categorías) --- */
            <motion.div key="examenes" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }}>
               {/* ... (Buscador y Lógica de exámenes igual que antes) ... */}
               <div className="max-w-4xl mx-auto mb-12">
                <div className="bg-white border border-[#211c52]/20 rounded-2xl p-2 flex items-center gap-3 shadow-xl">
                  <div className="bg-[#211c52] text-white p-3 rounded-xl shadow-[0_0_15px_rgba(33,28,82,0.2)]">
                    <Search size={20} strokeWidth={3} />
                  </div>
                  <input 
                    type="text" 
                    value={searchTerm}
                    placeholder="Buscar examen clínico..."
                    className="flex-grow bg-transparent border-none outline-none text-brand-dark placeholder:text-brand-dark/30 text-sm font-body font-medium"
                    onChange={(e) => setSearchBar(e.target.value)}
                  />
                  {searchTerm && <button onClick={() => setSearchBar("")}><X size={18} className="text-brand-dark/40 mr-2" /></button>}
                </div>
              </div>

              {searchTerm ? (
                 <div className="bg-white rounded-[40px] p-8 md:p-12 shadow-2xl border-2 border-[#211c52]/10">
                    <h2 className="font-display text-3xl font-bold text-[#211c52] mb-8 flex items-center gap-3">
                        <Activity className="text-[#211c52]" /> Resultados para "{searchTerm}"
                    </h2>
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {filteredResults.map((res, i) => (
                        <ExamCard key={i} name={res.name} category={res.categoryTitle} preparations={labsyso.preparations} onShowPrep={setShowPrep} />
                        ))}
                    </div>
                 </div>
              ) : (
                <div className="space-y-6 lg:space-y-0 lg:grid lg:grid-cols-12 lg:gap-8 lg:items-start">
                  {/* CATEGORÍAS - Lado izquierdo en desktop, arriba en móvil */}
                  <div className="lg:col-span-4 space-y-4">
                    {labsyso.categories.map((cat) => (
                      <div key={cat.id}>
                        <button
                          onClick={() => handleCategoryToggle(cat.id)}
                          className={`w-full text-left p-6 rounded-[28px] flex items-center justify-between border transition-all ${
                            activeCategoryId === cat.id ? "bg-[#211c52] text-white shadow-lg" : "bg-white text-brand-dark hover:shadow-md"
                          }`}
                        >
                          <span className="font-bold text-sm">{cat.title}</span>
                          <ChevronDown size={18} className={`transition-transform ${activeCategoryId === cat.id ? "rotate-180" : ""}`} />
                        </button>
                        
                        {/* ACORDEÓN EN MÓVIL - Se muestra bajo el botón (render condicionado por isDesktop) */}
                        <AnimatePresence>
                          {!isDesktop && activeCatId === cat.id && (
                            <motion.div 
                              initial={{ opacity: 0, height: 0 }} 
                              animate={{ opacity: 1, height: "auto" }} 
                              exit={{ opacity: 0, height: 0 }}
                              transition={{ duration: 0.28 }}
                              className="overflow-hidden"
                            >
                              <div className="mt-4 bg-white rounded-[50px] p-4 sm:p-6 border border-brand-soft">
                                <div className="mb-6 overflow-hidden rounded-[30px] bg-[#f5f7fb] flex-shrink-0 inline-block">
                                  <img
                                    src={cat.image}
                                    alt={cat.title}
                                    className="block max-w-full h-auto object-contain object-center"
                                  />
                                </div>
                                <div className="space-y-3">
                                  {cat.exams.map((exam, i) => (
                                    <ExamCard key={i} name={exam} preparations={labsyso.preparations} onShowPrep={setShowPrep} />
                                  ))}
                                </div>
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    ))}
                  </div>
                  
                  {/* CONTENIDO - Lado derecho en desktop, oculto en móvil */}
                  {/* CONTENIDO - Vista desktop (render condicionado por isDesktop para forzar re-render al cambiar tamaño) */}
                  {isDesktop && (
                    <div className="lg:col-span-8">
                      <AnimatePresence>
                        {activeCategoryId && (
                          <motion.div key={activeCategoryId} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} className="bg-white rounded-[50px] p-6 sm:p-8 shadow-sm border border-brand-soft sticky top-32">
                            <div className="mb-8 overflow-hidden rounded-[40px] bg-[#f5f7fb] flex-shrink-0 inline-block">
                              <img
                                src={labsyso.categories.find(c => c.id === activeCategoryId)?.image}
                                alt={labsyso.categories.find(c => c.id === activeCategoryId)?.title}
                                className="block max-w-full h-auto object-contain object-center"
                              />
                            </div>
                            <div className="grid lg:grid-cols-2 gap-4">
                              {labsyso.categories.find(c => c.id === activeCategoryId)?.exams.map((exam, i) => (
                                <ExamCard key={i} name={exam} preparations={labsyso.preparations} onShowPrep={setShowPrep} />
                              ))}
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  )}
                </div>
              )}
            </motion.div>
          ) : activeMainTab === "laboratorio" ? (
            /* --- VISTA LABORATORIO --- */
            <motion.div key="laboratorio" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }} className="max-w-6xl mx-auto space-y-12">
              
              {/* QUIÉNES SOMOS */}
              <section className="space-y-6">
                <div className="flex items-center gap-4">
                    <Users className="text-[#211c52]" size={40} />
                    <h2 className="text-5xl font-black text-[#211c52] tracking-tighter">¿Quiénes Somos?</h2>
                </div>
                <p className="text-lg text-gray-700 leading-relaxed text-justify font-medium">
                    {labsyso.about.quienes_somos}
                </p>
                <div className="relative group">
                    <div className="absolute -inset-2 bg-[#211c52]/10 rounded-[48px] blur-xl opacity-50" />
                    <div className="relative aspect-[16/9] rounded-[40px] overflow-hidden shadow-2xl border-[10px] border-white bg-white">
                      <img 
                          src={labsyso.about.imaben_labsyso} 
                          alt="Laboratorio Labsyso" 
                          className="absolute inset-0 w-full h-full object-contain object-center"
                      />
                    </div>
                </div>
              </section>

              {/* MISIÓN Y VISIÓN */}
              <section className="grid md:grid-cols-2 gap-6">
                <div className="bg-white p-10 rounded-[45px] border border-gray-100 shadow-xl shadow-gray-200/50">
                    <div className="w-14 h-14 bg-[#211c52]/10 rounded-2xl flex items-center justify-center text-[#211c52] mb-6">
                        <Target size={32} />
                    </div>
                    <h3 className="text-4xl font-bold text-[#211c52] mb-4">Misión</h3>
                    <p className="text-gray-600 text-lg leading-relaxed text-justify font-medium">
                        {labsyso.about.mision}
                    </p>
                </div>
                <div className="bg-white p-10 rounded-[45px] border border-gray-100 shadow-xl shadow-gray-200/50">
                    <div className="w-14 h-14 bg-[#211c52]/10 rounded-2xl flex items-center justify-center text-[#211c52] mb-6">
                        <Eye size={32} />
                    </div>
                    <h3 className="text-4xl font-bold text-[#211c52] mb-4">Visión</h3>
                    <p className="text-gray-600 text-lg leading-relaxed text-justify font-medium">
                        {labsyso.about.vision}
                    </p>
                </div>
              </section>

              {/* VALORES (CON ICONOGRAFÍA ESPECÍFICA) */}
              <section className="bg-white rounded-[60px] p-12 border border-gray-100 shadow-2xl shadow-gray-200/40">
                <div className="text-center mb-16">
                    <h2 className="text-4xl font-black text-[#211c52] mb-4">Nuestros Valores</h2>
                    <div className="w-24 h-1.5 bg-[#211c52] mx-auto rounded-full" />
                </div>
                <div className="grid md:grid-cols-3 gap-y-16 gap-x-12">
                    {labsyso.about.valores.map((val, idx) => (
                        <motion.div 
                          key={idx} 
                          whileHover={{ y: -5 }}
                          className="flex flex-col items-center text-center group"
                        >
                            <div className="w-20 h-20 bg-gray-50 text-[#211c52] rounded-[24px] flex items-center justify-center mb-6 shadow-sm border border-gray-100 group-hover:bg-[#211c52] group-hover:text-white transition-all duration-300">
                                {getValueIcon(val.titulo)}
                            </div>
                            <h4 className="text-2xl font-bold text-[#211c52] mb-3">{val.titulo}</h4>
                            <p className="text-gray-500 leading-snug max-w-[250px] font-medium">
                                {val.desc}
                            </p>
                        </motion.div>
                    ))}
                </div>
              </section>

              {/* BUZÓN DE QUEJAS — requisito auditoría */}
              <section className="bg-[#221d52] rounded-[48px] p-8 md:p-12 text-white shadow-2xl relative overflow-hidden">
                <div className="absolute top-0 right-0 w-48 h-48 bg-[#f5b419]/10 blur-3xl rounded-full" />
                <div className="relative z-10 grid md:grid-cols-2 gap-8 items-center">
                  <div>
                    <span className="inline-block text-[#f5b419] text-[10px] font-bold uppercase tracking-[0.3em] mb-3">
                      {LABSYSO_FEEDBACK.formCode}
                    </span>
                    <h2 className="font-display text-3xl md:text-4xl font-black mb-4 leading-tight">
                      {LABSYSO_FEEDBACK.title}
                    </h2>
                    <p className="text-white/80 leading-relaxed mb-6">
                      Puede enviar sugerencias, quejas o reclamos en línea y consultar el flujograma
                      oficial del procedimiento de atención, plazos y canales habilitados.
                    </p>
                    <div className="flex flex-wrap gap-3">
                      <button
                        type="button"
                        onClick={() => setFeedbackOpen(true)}
                        className="inline-flex items-center gap-2 bg-[#f5b419] text-[#221d52] px-6 py-3.5 rounded-2xl font-bold text-xs uppercase tracking-widest hover:brightness-105 transition-all active:scale-[0.98]"
                      >
                        Abrir buzón
                      </button>
                      <a
                        href={LABSYSO_FEEDBACK.flowchartPdfUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 border border-white/25 text-white px-6 py-3.5 rounded-2xl font-bold text-xs uppercase tracking-widest hover:bg-white/10 transition-colors"
                      >
                        Ver flujograma PDF
                      </a>
                    </div>
                  </div>
                  <div className="bg-white/5 border border-white/10 rounded-[32px] p-6 space-y-3 text-sm text-white/85">
                    <p className="font-bold text-[#f5b419] uppercase tracking-widest text-xs">Canales 6.2</p>
                    <ul className="space-y-2 list-disc pl-5 marker:text-[#f5b419]">
                      <li>Formulario en línea (este sitio)</li>
                      <li>Correo electrónico y redes sociales</li>
                      <li>Llamadas telefónicas</li>
                      <li>Comunicación directa con el personal</li>
                    </ul>
                    <p className="text-xs text-white/60 pt-2 border-t border-white/10">
                      Análisis máximo: {LABSYSO_FEEDBACK.analysisDeadlineDays} días hábiles desde la recepción.
                    </p>
                  </div>
                </div>
              </section>

            </motion.div>
          ) : (
            /* --- VISTA CREDENCIALES --- */
            <motion.div key="credenciales" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }} className="max-w-6xl mx-auto space-y-8">
              <div className="flex items-center gap-4">
                <Award className="text-[#211c52]" size={40} />
                <h2 className="text-4xl md:text-5xl font-black text-[#211c52] tracking-tighter">
                  Acreditaciones <span className="font-light">y Certificaciones</span>
                </h2>
              </div>
              <p className="text-lg text-gray-700 leading-relaxed font-medium">
                Documentación oficial de nuestras acreditaciones, certificaciones y reconocimientos institucionales.
              </p>
              <div className="bg-white rounded-[40px] overflow-hidden shadow-2xl border border-gray-100 p-2">
                <div style={{ position: "relative", paddingTop: "max(60%, 324px)", width: "100%", height: 0 }}>
                  <ConsentAwareEmbed
                    title="Catálogo Heyzine"
                    description="El catálogo se carga desde Heyzine (tercero). Actívelo si acepta cookies de contenido externo."
                    className="absolute inset-0 rounded-none h-full border-0"
                    minHeight="100%"
                  >
                    <iframe
                      style={{ position: "absolute", border: "none", width: "100%", height: "100%", left: 0, top: 0 }}
                      src={labsysoCatalogUrl}
                      title="AGDYSA - Certificaciones y Acreditaciones"
                      seamless="seamless"
                      scrolling="no"
                      frameBorder="0"
                      allowTransparency="true"
                      allowFullScreen={true}
                    />
                  </ConsentAwareEmbed>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </main>

      {/* MODAL DE PREPARACIÓN */}
      <AnimatePresence>
        {showPrep && (
          <PreparationModal name={showPrep.label} text={labsyso.preparations[showPrep.key]} onClose={() => setShowPrep(null)} />
        )}
      </AnimatePresence>

      <LabsysoFeedbackWidget
        open={feedbackOpen}
        onOpenChange={setFeedbackOpen}
        logoUrl={labsyso.hero.labsyso_logo}
        socialMedia={labsyso.socialMedia}
      />
    </motion.div>
  );
};

// ... (Componentes ExamCard y PreparationModal iguales que en tu código original)
const ExamCard = ({ name, category, preparations, onShowPrep }) => {
    const prepKey = name.split(" ")[0];
    return (
      <div className="flex items-center justify-between p-4 rounded-2xl bg-[#f8faf9] border border-transparent hover:border-[#211c52]/20 hover:bg-white hover:shadow-lg transition-all group">
        <div className="flex-grow pr-4">
          <span className="font-body font-semibold text-brand-dark/90 text-sm tracking-tight block leading-tight">
            {name}
          </span>
          {category && (
            <span className="block text-[10px] font-body font-bold text-[#211c52] uppercase mt-1 opacity-60">
              {category}
            </span>
          )}
        </div>
        {preparations[prepKey] && (
          <button 
            onClick={() => onShowPrep({ key: prepKey, label: name })} 
            className="p-2 bg-white shadow-sm rounded-xl text-[#211c52] hover:bg-[#211c52] hover:text-white transition-all"
          >
            <Info size={14} />
          </button>
        )}
      </div>
    );
  };
  
  const PreparationModal = ({ name, text, onClose }) => {
    const paragraphs = Array.isArray(text)
      ? text
      : typeof text === "string"
      ? text
          .split(/\r?\n+/)
          .map((line) => line.trim())
          .filter(Boolean)
      : [];

    const renderInlineText = (value) => {
      // Primero divide por \n para mantener líneas simples dentro del párrafo
      const lines = value.split('\n');
      
      return lines.map((line, lineIdx) => (
        <span key={lineIdx}>
          {lineIdx > 0 && <br />}
          {
            // Dentro de cada línea, procesa bold y underline
            line.split(/(\*\*[^*]+\*\*|__[^_]+__)/g)
              .filter(Boolean)
              .map((token, tokenIdx) => {
                if (/^\*\*.+\*\*$/.test(token)) {
                  return (
                    <strong key={`${lineIdx}-${tokenIdx}`} className="font-bold text-brand-dark">
                      {token.slice(2, -2)}
                    </strong>
                  );
                }
                if (/^__.+__$/.test(token)) {
                  return (
                    <em key={`${lineIdx}-${tokenIdx}`} className="italic text-brand-dark">
                      {token.slice(2, -2)}
                    </em>
                  );
                }
                return <span key={`${lineIdx}-${tokenIdx}`}>{token}</span>;
              })
          }
        </span>
      ));
    };

    return (
      <motion.div 
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} 
        className="fixed inset-0 z-[2000] bg-[#211c52]/90 backdrop-blur-xl flex items-center justify-center p-4 sm:p-6 md:p-10"
        onClick={onClose}
      >
        <motion.div 
          initial={{ scale: 0.9, y: 20 }} animate={{ scale: 1, y: 0 }} 
          className="bg-white rounded-[40px] w-full max-w-4xl p-6 sm:p-8 md:p-10 relative overflow-hidden overflow-y-auto max-h-[calc(100vh-4rem)]"
          onClick={e => e.stopPropagation()}
        >
          <button
            className="absolute top-4 right-4 z-20 p-3 rounded-full bg-[#211c52]/10 text-[#211c52] shadow-sm hover:bg-[#211c52]/15 transition-colors"
            onClick={onClose}
            aria-label="Cerrar"
          >
            <X size={18} />
          </button>
          <div className="text-left">
            <div className="w-16 h-16 bg-[#211c52]/10 rounded-2xl flex items-center justify-center mx-auto mb-6 text-[#211c52]">
              <ClipboardCheck size={28} />
            </div>
            <h3 className="font-display text-3xl font-bold text-[#211c52] mb-4 text-center">Instrucciones: {name}</h3>
            <div className="space-y-6 text-brand-dark/70 text-lg leading-relaxed mb-8 border-t border-brand-soft pt-6">
              {paragraphs.map((paragraph, index) => (
                <p key={index}>{renderInlineText(paragraph)}</p>
              ))}
            </div>
            <button 
              className="w-full bg-[#211c52] text-white py-4 rounded-2xl font-bold shadow-xl active:scale-95 transition-all cursor-pointer" 
              onClick={onClose}
            >
              Entendido
            </button>
          </div>
        </motion.div>
      </motion.div>
    );
  };

export default LabsysoPage;
/*<!--© 2026 Todos los derechos reservados Desarrollado por JEMAprod-Tecnologia-->*/